import classNames from "classnames"
import React from "react"

import Link from "./Link"

import GatsbyStoryblokImage from "@components/elements/V2/GatsbyStoryblokImage"
import ArrowIcon from "@components/elements/V2/Icons/ArrowIcon"
import Typography from "@components/elements/V2/Typography"
import { Color } from "@constants/V2/color"

type Props = {
  link: string | YocoCom.YocoLink
  imageUrl?: string
  imageAlt?: string
  text: string
  description?: string
  badgeText?: string
  badgeColor?: Color
  size?: "small" | "medium" | "large"
  onClick?: React.MouseEventHandler<HTMLElement>
  className?: string
}

const ImageLink = ({
  link,
  imageUrl,
  imageAlt,
  text,
  description,
  badgeText,
  badgeColor = Color.Charcoal,
  size = "small",
  onClick,
  className,
}: Props) => {
  const animateClassnames =
    "transition-all duration-200 lg:translate-x-[-20px] lg:group-hover:translate-x-0"

  return (
    <Link
      link={link}
      className={classNames(
        size === "small" || size === "medium" ? "items-start" : "items-center",
        "group flex w-fit gap-10",
        className
      )}
      onClick={onClick}
    >
      {imageUrl && (
        <GatsbyStoryblokImage
          aspectRatio="1:1"
          image={imageUrl}
          alt={imageAlt || text}
          className={classNames(
            size === "small" && "h-[24px] w-[24px]",
            size === "medium" && "h-[35px] w-[35px]",
            size === "large" && "h-[42px] w-[42px]",
            "rounded-[4px] lg:max-h-104"
          )}
        />
      )}
      <div className="flex flex-col gap-[2px]">
        <div className="flex items-center gap-[5px]">
          <Typography
            color={Color.Charcoal}
            font="grotesk"
            weight="medium"
            size="body-md-fixed"
            text={text}
            className={classNames(
              "duration-200 ease-in-out group-hover:text-blue"
            )}
          />
          <ArrowIcon
            className={classNames(
              "-mt-[2px] group-hover:text-blue lg:opacity-0 lg:group-hover:opacity-100",
              animateClassnames
            )}
            width="12"
            height="12"
          />
          {badgeText && (
            <Typography
              color={badgeColor}
              font="grotesk"
              weight="book"
              size="subscript-md"
              text={badgeText}
              className={classNames("ml-4 uppercase", animateClassnames)}
            />
          )}
        </div>
        {description && (
          <Typography
            color={Color.Charcoal}
            font="grotesk"
            weight="book"
            size="subscript-sm"
            text={description}
            className={"opacity-70"}
          />
        )}
      </div>
    </Link>
  )
}

export default ImageLink
